<template>
  <div class="bulk-analysis-view">
    <div class="page-header">
      <h1 class="page-title">Analisi in Blocco</h1>
      <p class="page-description">
        Carica un file Excel o CSV per analizzare multiple aziende contemporaneamente.
      </p>
    </div>

    <!-- Active Job Warning -->
    <div v-if="hasActiveJob && !selectedJob" class="active-job-notice">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="notice-icon">
        <circle cx="12" cy="12" r="10" stroke-width="2"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke-width="2" stroke-linecap="round"/>
        <line x1="12" y1="16" x2="12.01" y2="16" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span>Hai un job attivo in elaborazione. Attendi il completamento prima di avviarne uno nuovo.</span>
    </div>

    <!-- Main Content -->
    <div class="content-area">
      <!-- Job Detail View -->
      <template v-if="selectedJob">
        <BulkJobDetail
          :job="selectedJob"
          @back="selectedJob = null"
          @updated="refreshCurrentJob"
        />
      </template>

      <!-- Jobs List View -->
      <template v-else>
        <div class="actions-bar">
          <button
            @click="showUploadModal = true"
            class="upload-button"
            :disabled="hasActiveJob"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="button-icon">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="17,8 12,3 7,8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="3" x2="12" y2="15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Carica Nuovo File
          </button>
        </div>

        <BulkJobsList
          :jobs="bulkJobs"
          :loading="loading"
          :current-page="currentPage"
          :total-pages="totalPages"
          @select="selectJob"
          @download="handleDownload"
          @delete="handleDelete"
          @refresh="loadBulkJobs"
          @page="handlePageChange"
        />
      </template>
    </div>

    <!-- Upload Modal -->
    <BulkUploadModal
      :is-open="showUploadModal"
      @close="showUploadModal = false"
      @created="handleJobCreated"
    />
  </div>
</template>

<script>
import { useBulkAnalysisStore } from '@/stores/bulkAnalysisStore';
import { useAuthStore } from '@/stores/authStore';
import { BulkUploadModal, BulkJobsList, BulkJobDetail } from '@/components/BulkAnalysis';

export default {
  name: 'BulkAnalysisView',
  components: {
    BulkUploadModal,
    BulkJobsList,
    BulkJobDetail,
  },
  data() {
    return {
      showUploadModal: false,
      selectedJob: null,
      loading: false,
    };
  },
  computed: {
    userId() {
      const authStore = useAuthStore();
      return authStore.userId;
    },
    bulkJobs() {
      const store = useBulkAnalysisStore();
      return store.bulkJobs;
    },
    currentPage() {
      const store = useBulkAnalysisStore();
      return store.currentPage;
    },
    totalPages() {
      const store = useBulkAnalysisStore();
      return store.totalPages;
    },
    hasActiveJob() {
      const store = useBulkAnalysisStore();
      return store.hasActiveJob;
    },
  },
  mounted() {
    this.loadBulkJobs();
    this.startPollingActiveJobs();
  },
  beforeUnmount() {
    const store = useBulkAnalysisStore();
    store.stopAllPolling();
  },
  methods: {
    async loadBulkJobs() {
      if (!this.userId) return;

      this.loading = true;
      try {
        const store = useBulkAnalysisStore();
        await store.fetchUserBulkJobs(this.userId);
      } catch (error) {
        console.error('Error loading bulk jobs:', error);
      } finally {
        this.loading = false;
      }
    },

    startPollingActiveJobs() {
      const store = useBulkAnalysisStore();

      // Poll active jobs
      for (const job of this.bulkJobs) {
        if (['pending', 'processing', 'paused'].includes(job.status)) {
          store.startPolling(job.job_id, this.userId, (updatedJob) => {
            // If viewing this job, update it
            if (this.selectedJob && this.selectedJob.job_id === updatedJob.job_id) {
              this.selectedJob = updatedJob;
            }
          });
        }
      }
    },

    selectJob(job) {
      this.selectedJob = job;
    },

    async refreshCurrentJob() {
      if (!this.selectedJob) return;

      const store = useBulkAnalysisStore();
      try {
        this.selectedJob = await store.fetchBulkJobById(
          this.selectedJob.job_id,
          this.userId
        );
      } catch (error) {
        console.error('Error refreshing job:', error);
      }
    },

    async handleJobCreated(result) {
      this.showUploadModal = false;
      await this.loadBulkJobs();

      // Start polling the new job
      const store = useBulkAnalysisStore();
      store.startPolling(result.job_id, this.userId);

      // Optionally show the new job
      const newJob = this.bulkJobs.find(j => j.job_id === result.job_id);
      if (newJob) {
        this.selectedJob = newJob;
      }
    },

    async handleDownload(job) {
      const store = useBulkAnalysisStore();
      try {
        await store.downloadResults(job.job_id, this.userId);
      } catch (error) {
        console.error('Error downloading results:', error);
        alert('Errore durante il download dei risultati.');
      }
    },

    async handleDelete(job) {
      if (!confirm(`Sei sicuro di voler eliminare il job "${job.job_name}"?`)) {
        return;
      }

      const store = useBulkAnalysisStore();
      try {
        await store.deleteJob(job.job_id, this.userId);
        await this.loadBulkJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Errore durante l\'eliminazione del job.');
      }
    },

    handlePageChange(page) {
      const store = useBulkAnalysisStore();
      store.setCurrentPage(page);
      this.loadBulkJobs();
    },
  },
};
</script>

<style scoped>
.bulk-analysis-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.page-description {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

/* Active Job Notice */
.active-job-notice {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  color: #b45309;
}

.notice-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Content Area */
.content-area {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
}

/* Actions Bar */
.actions-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  background: #2e4eed;
  border: 2px solid #2e4eed;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-button:hover:not(:disabled) {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.upload-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-icon {
  width: 20px;
  height: 20px;
}

@media (max-width: 768px) {
  .bulk-analysis-view {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .content-area {
    padding: 1rem;
  }

  .actions-bar {
    justify-content: stretch;
  }

  .upload-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
